import { ArrowRightOutlined, Button, DownOutlined, message } from 'gokwik-ui-kit'
import KpShopifyImg from '@library/images/common/kwikpass-onboarding-images/kp-icon.png'
import KpShopifyRatings from '@library/images/common/kwikpass-onboarding-images/kp-ratings.png'
import Engage from '@library/images/icons/engage.svg'
import Search from '@library/images/icons/search.svg'
import Refresh from '@library/images/icons/refresh.svg'
import { BANNER_LOGOS, case_study_cards, FAQS, kp_banners, kp_posters } from './constants'
import { useEffect, useState } from 'react'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'

// Feature Card Component
function FeatureCard({ icon, title, description }) {
    return (
        <div className='border border-[#eaecf0] rounded-lg p-6'>
            <div className='bg-[#ebf6ff] w-10 h-10 rounded-lg flex items-center justify-center mb-4'>
                <span className='text-xl'>{icon}</span>
            </div>
            <h3 className='text-lg font-semibold mb-2'>{title}</h3>
            <p className='text-[#667085] text-sm'>{description}</p>
        </div>
    )
}

// FAQ Item Component
function FaqItem({ question, answer, isOpen, onClick }) {
    return (
        <div className='border-b border-[#eaecf0]'>
            <div className='flex justify-between items-center cursor-pointer' onClick={onClick}>
                <h3 className='font-medium'>{question}</h3>
                <DownOutlined
                    className={`h-5 w-5 text-[#667085] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>
            {isOpen && (
                <div className='mt-2 text-[#667085]'>
                    <p>{answer}</p>
                </div>
            )}
        </div>
    )
}

export default function KwikpassBanner() {
    const [openIndex, setOpenIndex] = useState(0)
    const userData = useSelector(getUserData)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [posterIndex, setPosterIndex] = useState(0)
    const [isButtonDisabled, setIsButtonDisabled] = useState(false)

    useEffect(() => {}, [])

    useEffect(() => {
        logEvent(
            'product_pitch_page_viewed',
            'page',
            'kwikpass-product-pitch',
            userData?.userDetails?.email,
            userData?.merchantDetails?.m_id,
        )
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % kp_banners.length)
        }, 3000)

        const posterInterval = setInterval(() => {
            setPosterIndex((prev) => (prev + 1) % kp_posters.length)
        }, 3000)
        return () => {
            clearInterval(interval)
            clearInterval(posterInterval)
        }
    }, [])

    const handleToggle = (index) => {
        setOpenIndex((prevIndex) => (prevIndex === index ? null : index))
    }

    const handleInstallKp = (position) => {
        logEvent(
            'product_pitch_install_cta_clicked',
            'click',
            'kwikpass-product-pitch',
            userData?.userDetails?.email,
            userData?.merchantDetails?.m_id,
        )
        window
            .open(`https://apps.shopify.com/kwikpass?utm_source=dashboard_lp&utm_medium=${position}`, '_blank')
            .focus()
    }

    const handleCardClick = (url) => {
        window.open(url, '_blank').focus()
    }

    const handleManageKp = () => {
        logEvent(
            'product_pitch_interested_clicked',
            'click',
            'kwikpass-product-pitch',
            userData?.userDetails?.email,
            userData?.merchantDetails?.m_id,
        )
        setIsButtonDisabled(true)
        message.success('Thank you for showing interest! Our team will get back to you soon.')
    }

    return (
        <div className='flex flex-col bg-white'>
            <div className='p-6'>
                <img src={KpShopifyImg} className='w-auto h-10 mr-2'></img>
                {/* Hero Section */}
                <div className='flex flex-row mb-4'>
                    <div className='w-1/2 pr-8'>
                        <h1 className='text-4xl font-bold mb-4'>
                            Turn Every Visitor into a <span className='text-[#1570ef]'>Paying Customer</span>
                        </h1>
                        <p className='text-[#475467] mb-4 leading-7'>
                            Effortlessly and securely identify visitors with phone-first logins and smart pop-ups.
                            Convert anonymous browsers into loyal customers and drive revenue with the most secure
                            Shopify customer identification platform.
                        </p>
                        <div className='flex items-center mt-8'>
                            <Button
                                className='!text-primary px-4 py-2 rounded-md flex items-center border-[#004b8d] text-[#004b8d] h-10'
                                onClick={() => handleInstallKp('first-fold')}
                                variant='default'
                            >
                                <img
                                    src='https://www.gokwik.co/assets/images/kwikpass/shop.png'
                                    loading='lazy'
                                    className='w-5 h-5 mr-2'
                                />
                                Install KwikPass
                            </Button>
                            <Button
                                onClick={handleManageKp}
                                className='ml-4 font-semibold no-underline h-10'
                                variant='primary'
                                disabled={isButtonDisabled}
                            >
                                I'm interested
                            </Button>
                        </div>
                        <div className='mt-8'>
                            <img src={KpShopifyRatings} className='w-auto h-auto mr-2'></img>
                        </div>
                    </div>
                    <div className='w-1/2 flex justify-end mt-[-70px] overflow-hidden'>
                        <div
                            className='flex transition-transform duration-700 ease-in-out'
                            style={{ transform: `translateX(-${posterIndex * 100}%)` }}
                        >
                            {kp_posters.map((img, index) => (
                                <img
                                    key={index}
                                    loading='lazy'
                                    src={img}
                                    alt={`Banner ${index + 1}`}
                                    className='w-full h-[500px] flex-shrink-0 object-contain'
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Trusted Brands */}
                <div className='mb-24 overflow-hidden'>
                    <p className='text-sm text-[#475467] mb-4'>Trusted by 10,000+ Brands</p>
                    <style>
                        {`
                            @keyframes scroll {
                                0% { transform: translateX(0%); }
                                100% { transform: translateX(-50%); }
                            }
                        `}
                    </style>
                    <div className='relative w-full overflow-hidden'>
                        <div
                            className='flex'
                            style={{
                                animation: 'scroll 20s linear infinite',
                                width: '200%',
                            }}
                        >
                            {BANNER_LOGOS.concat(BANNER_LOGOS).map((logoSrc, i) => (
                                <div
                                    key={i}
                                    className='opacity-60 flex items-center justify-center px-6'
                                    style={{ flex: '0 0 auto' }}
                                >
                                    <img src={logoSrc} alt={`Brand logo ${i + 1}`} width={100} height={30} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='text-center mt-20'>
                        <h2 className='text-3xl font-bold text-black mb-2'>GoKwik Network Effect</h2>
                        <p className='text-gray-500 mb-8 text-base'>
                            Leveraging the deepest pool of shoppers to build industry-best solutions with AI-powered
                            website visitor tracking tools
                        </p>

                        <div
                            className='relative rounded-xl shadow-sm px-6 py-5 flex w-11/12 justify-center mx-auto'
                            style={{
                                background:
                                    'linear-gradient(270deg, rgba(247, 157, 28, 0.1) 0%, rgba(247, 157, 28, 0.04) 50%, rgba(247, 157, 28, 0.1) 100%)',
                                overflow: 'hidden', // Ensures the inner content respects the border-radius
                            }}
                        >
                            {/* Pseudo-element for the gradient border */}
                            <div
                                className='absolute inset-0 rounded-xl'
                                style={{
                                    border: '1px solid transparent', // Transparent border to ensure space for gradient
                                    backgroundImage:
                                        'linear-gradient(90deg, #F79D1C 0%, rgba(247, 157, 28, 0) 12%, rgba(247, 157, 28, 0) 50%, rgba(247, 157, 28, 0) 88%, #F79D1C 100%)',
                                    backgroundClip: 'border-box', // Clips the background to the border area
                                    zIndex: -1, // Ensures the border is behind the content
                                }}
                            ></div>

                            <div className='flex gap-16 justify-between'>
                                <div className='flex gap-2 justify-center items-center'>
                                    <div className='text-[#F79D1C] font-bold text-3xl'>25%</div>
                                    <div className='text-gray-500 text-base text-left'>
                                        of anonymous user <br /> sessions identified
                                    </div>
                                </div>
                                <div className='flex gap-1 justify-center items-center w-px h-6 bg-[#F79E1E] m-auto'></div>

                                <div className='flex gap-2 justify-center items-center'>
                                    <div className='text-[#F79D1C] font-bold text-3xl'>5–10X</div>
                                    <div className='text-gray-500 text-base text-left'>
                                        increase <br />
                                        in ROAS
                                    </div>
                                </div>

                                <div className='flex gap-1 justify-center items-center w-px h-6 bg-[#F79E1E] m-auto'></div>
                                <div className='flex gap-2 justify-center items-center'>
                                    <div className='text-[#F79D1C] font-bold text-3xl'>130Mn+</div>
                                    <div className='text-gray-500 text-base text-left'>
                                        Shoppers on <br /> GoKwik network
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div className='mb-16 mt-24 bg-[#E6EDF4] py-12'>
                    <div className='flex justify-center mb-6'>
                        <div className='flex items-center'>
                            <div className='flex items-center  px-4 py-2 rounded-md text-xl font-semibold'>
                                <span className='mr-2'>
                                    <img src={Search} />
                                </span>
                                <span>Identify</span>
                            </div>
                            <ArrowRightOutlined className='mx-4 h-4 w-4 text-[#667085]' />
                            <div className='flex items-center px-4 py-2 rounded-md text-xl font-semibold'>
                                <span className='mr-2'>
                                    <img src={Engage} />
                                </span>
                                <span>Engage</span>
                            </div>
                            <ArrowRightOutlined className='mx-4 h-4 w-4 text-[#667085]' />
                            <div className='flex items-center  px-4 py-2 rounded-md text-xl font-semibold'>
                                <span className='mr-2'>
                                    <img src={Refresh} />
                                </span>
                                <span>Convert</span>
                            </div>
                        </div>
                    </div>
                    <h2 className='text-3xl font-bold text-center mb-2'>The Smarter Way to Recognise Every Shopper</h2>
                    <p className='text-grey-500 text-center mb-8 text-base'>
                        With Kwik Pass, the most advanced website visitor tracking tech, every click counts towards
                        higher growth.
                    </p>
                    <div className='overflow-hidden w-full flex justify-center mt-8 mb-16'>
                        <div
                            className='flex transition-transform duration-700 ease-in-out'
                            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                        >
                            {kp_banners.map((img, index) => (
                                <img
                                    key={index}
                                    loading='lazy'
                                    src={img}
                                    alt={`Banner ${index + 1}`}
                                    className='w-full h-[500px] flex-shrink-0 object-contain'
                                />
                            ))}
                        </div>
                    </div>
                    <div className='flex items-center justify-center mt-8'>
                        <Button
                            className='!text-primary px-4 py-2 rounded-md flex items-center border-[#004b8d] text-[#004b8d] h-10'
                            onClick={() => handleInstallKp('second-fold')}
                            variant='default'
                        >
                            <img
                                src='https://www.gokwik.co/assets/images/kwikpass/shop.png'
                                loading='lazy'
                                className='w-5 h-5 mr-2'
                            />
                            Install KwikPass
                        </Button>
                        <Button
                            onClick={handleManageKp}
                            className='ml-4 font-semibold no-underline h-10'
                            variant='primary'
                            disabled={isButtonDisabled}
                        >
                            I'm interested
                        </Button>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className='mb-16 bg-white max-w-3xl flex flex-col p-6 rounded-md m-auto'>
                    <h2 className='text-2xl font-bold text-center mb-8'>Frequently Asked Questions</h2>
                    <div className='max-w-3xl'>
                        {FAQS.map((faq, index) => (
                            <FaqItem
                                key={index}
                                question={faq.question}
                                answer={faq.answer}
                                isOpen={openIndex === index}
                                onClick={() => handleToggle(index)}
                            />
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className='bg-[#F9FAFB] p-10 rounded-xl flex flex-col flex-wrap justify-center gap-6'>
                    <p className='text-3xl text-center font-bold mb-4'>Resources</p>
                    <div className='flex justify-center gap-6'>
                        {case_study_cards.map((card, index) => (
                            <div
                                key={index}
                                className='relative group bg-white p-4 rounded-lg shadow-md w-[280px] transition-transform duration-300 hover:-translate-y-2 cursor-pointer'
                                onClick={() => handleCardClick(card.redirectUrl)}
                            >
                                <img
                                    src={card.imgSrc}
                                    alt={`Card ${index}`}
                                    className='rounded-md w-full h-60 object-cover'
                                />
                                <div className='mt-4 text-sm font-semibold text-black'>{card.title}</div>

                                {/* Know more button visible only on hover if showLink is true */}
                                {card.showLink && (
                                    <div className='mt-2 '>
                                        <a className='text-sm text-black font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                                            Know more
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
